const TENANT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PROVIDER_TENANT_ID = "00000000-0000-0000-0000-000000000000";

type FetchImplementation = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

let selectedTenantId: string | null = null;
let tenantRequests = new AbortController();
let tenantGeneration = 0;

export interface TenantTransportSnapshot {
  tenantId: string | null;
  generation: number;
}

function normalizeTenantId(tenantId: string | null): string | null {
  if (tenantId === null || tenantId === PROVIDER_TENANT_ID) return null;
  const normalized = tenantId.trim().toLowerCase();
  if (!TENANT_ID_PATTERN.test(normalized)) {
    throw new Error("Invalid tenant identifier");
  }
  return normalized;
}

function combineAbortSignals(
  requestSignal: AbortSignal | null | undefined,
  tenantSignal: AbortSignal,
): { signal: AbortSignal; cleanup: () => void } {
  if (!requestSignal) {
    return { signal: tenantSignal, cleanup: () => undefined };
  }

  const combined = new AbortController();
  const abortFromRequest = () => combined.abort(requestSignal.reason);
  const abortFromTenant = () => combined.abort(tenantSignal.reason);

  if (requestSignal.aborted) abortFromRequest();
  else requestSignal.addEventListener("abort", abortFromRequest, { once: true });

  if (tenantSignal.aborted) abortFromTenant();
  else tenantSignal.addEventListener("abort", abortFromTenant, { once: true });

  return {
    signal: combined.signal,
    cleanup: () => {
      requestSignal.removeEventListener("abort", abortFromRequest);
      tenantSignal.removeEventListener("abort", abortFromTenant);
    },
  };
}

export function setSupabaseTenantId(tenantId: string | null): void {
  const normalized = normalizeTenantId(tenantId);
  if (normalized === selectedTenantId) return;

  tenantRequests.abort(
    new DOMException("Tenant context changed", "AbortError"),
  );
  tenantRequests = new AbortController();
  selectedTenantId = normalized;
  tenantGeneration += 1;
}

export function getSupabaseTenantId(): string | null {
  return selectedTenantId;
}

export function getSupabaseTenantSnapshot(): TenantTransportSnapshot {
  return {
    tenantId: selectedTenantId,
    generation: tenantGeneration,
  };
}

export function isSupabaseTenantSnapshotCurrent(
  snapshot: TenantTransportSnapshot,
): boolean {
  return (
    snapshot.tenantId === selectedTenantId &&
    snapshot.generation === tenantGeneration
  );
}

export function createTenantAwareFetch(
  baseFetch: FetchImplementation = globalThis.fetch.bind(globalThis),
): FetchImplementation {
  return async (input, init = {}) => {
    const request = new Request(input, init);
    const headers = new Headers(request.headers);
    if (selectedTenantId) headers.set("X-Tenant-Id", selectedTenantId);
    else headers.delete("X-Tenant-Id");

    const { signal, cleanup } = combineAbortSignals(
      request.signal,
      tenantRequests.signal,
    );
    try {
      return await baseFetch(request, { headers, signal });
    } finally {
      cleanup();
    }
  };
}
